import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import { useDirtyTab } from "./settings/SettingsDirtyContext";
import { Bell, UserPlus, MessageCircle, Bookmark, Flame } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
    {
        key: "new_follower",
        icon: UserPlus,
        label: "New followers",
        description: "When someone starts following you.",
    },
    {
        key: "message",
        icon: MessageCircle,
        label: "Direct messages",
        description: "When someone sends you a DM. Turn off to silence in-app message alerts (messages still arrive in your inbox).",
    },
    {
        key: "comment",
        icon: MessageCircle,
        label: "Comments on your adventures",
        description: "When someone replies to a trip report you posted.",
    },
    {
        key: "save",
        icon: Bookmark,
        label: "Saves",
        description: "When someone saves your adventure to recreate it.",
    },
    {
        key: "share_milestone",
        icon: Flame,
        label: "Share milestones",
        description: "When your trip report hits 10, 50, 100+ shares. Great for tracking virality.",
    },
];

export default function NotificationSettings({ embedded = false }) {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const initial = useMemo(() => {
        const current = user?.notification_preferences || {};
        return KINDS.reduce((acc, k) => {
            acc[k.key] = current[k.key] !== false;
            return acc;
        }, {});
    }, [user]);
    const [prefs, setPrefs] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isDirty = JSON.stringify(prefs) !== JSON.stringify(initial);

    const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/users/me/notification-preferences", prefs);
            updateUser({ notification_preferences: data.notification_preferences });
            toast.success("Notification preferences saved");
            if (!embedded) navigate("/settings");
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        } finally {
            setSaving(false);
        }
    };

    useDirtyTab("notifications", isDirty, save);

    const inner = (
        <>
            <p className="text-slate-gray mb-6">
                Mute notification types you don't want. The bell icon in your nav will only chime for the categories you leave on.
            </p>

            <div className="bg-snowcap border border-ridge-line rounded-lg divide-y divide-ridge-line">
                {KINDS.map(({ key, icon: Icon, label, description }) => (
                    <div
                        key={key}
                        data-testid={`notif-pref-row-${key}`}
                        className="flex items-start gap-4 px-5 py-4"
                    >
                        <div className="w-10 h-10 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-granite">{label}</div>
                            <p className="text-sm text-slate-gray leading-relaxed">{description}</p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={prefs[key]}
                            aria-label={`Toggle ${label}`}
                            data-testid={`notif-pref-toggle-${key}`}
                            onClick={() => toggle(key)}
                            className={`relative w-11 h-6 shrink-0 rounded-full transition-colors mt-1 ${
                                prefs[key] ? "bg-ember-orange" : "bg-ridge-line"
                            }`}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-snowcap shadow-sm transition-transform ${
                                    prefs[key] ? "translate-x-5" : "translate-x-0"
                                }`}
                            />
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-3 p-4 rounded-md bg-trail-tan/50 border border-trail-tan text-xs text-granite">
                <strong className="font-bold">Always on:</strong> welcome messages (one-shot onboarding). You won't see another after today.
            </div>

            <div className="flex gap-3 mt-6">
                {!embedded && (
                    <button
                        type="button"
                        data-testid="notif-pref-cancel"
                        onClick={() => navigate("/settings")}
                        className="px-6 py-3 rounded-md border border-ridge-line text-granite font-bold hover:bg-alpine-mist transition-all"
                    >
                        Cancel
                    </button>
                )}
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="notif-pref-save"
                    className="flex-1 py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all"
                >
                    {saving ? "Saving…" : "Save preferences"}
                </button>
            </div>
        </>
    );

    if (embedded) return <div>{inner}</div>;

    return (
        <div>
            <TopBar title="Notifications" />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:flex items-center gap-3 mb-8">
                    <Bell className="w-8 h-8 text-ember-orange" />
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">
                        Notification preferences
                    </h1>
                </div>
                {inner}
            </div>
        </div>
    );
}
